/**
 * Auflösung der E-Mail-Signatur des angemeldeten Nutzers.
 *
 * Die Logik lag bisher ausschließlich im Verfassen-Dialog. Seit die Signatur
 * auch beim Antworten im Lesebereich gesetzt wird (Kundenfeedback 14.09.26),
 * braucht sie eine gemeinsame Stelle — sonst driften die beiden Wege
 * auseinander und ein und derselbe Nutzer unterschreibt unterschiedlich.
 *
 * Regeln (unverändert übernommen):
 *  - Die Unternehmens-Signatur hat Vorrang vor der Organisations-Signatur;
 *    Kommunikation gilt je Unternehmen.
 *  - Der Standort bestimmt das Unternehmen und befüllt die
 *    Standort-Platzhalter.
 *  - Leere Angaben verschwinden samt Beschriftung und Trennzeichen
 *    (siehe utils/signatureTemplate).
 */
import { getRequestGeneric } from "@/services/backendApiGeneric";
import { useAuthStore } from "@/stores/authStore";
import { activeSignaturePhotoUrl, loadSignaturePhoto } from "@/services/signaturePhoto";
import { normalizePhone, renderSignatureHtml, signatureBlockHtml, withSignaturePhoto } from "@/utils/signatureTemplate";
import { signatureHtmlToText } from "@/utils/letterSignature";

/** Standort, wie ihn /protected/users/me/locations liefert. */
export interface SignatureLocation {
  id: number;
  idCompany?: number;
  name?: string;
  address?: string;
  streetNo?: string;
  zip?: string;
  city?: string;
  landline?: string;
  email?: string;
}

/** Organisationsweite Vorgaben (Fallback, wenn das Unternehmen nichts setzt). */
export interface SignatureDefaults {
  signature: string;
  orgName: string;
  orgHomepage: string;
}

/** Aufgelöste Vorlage samt der Quellen für {{organisation}} und {{webseite}}. */
export interface SignatureContext {
  template: string;
  companyName: string;
  website: string;
  location: SignatureLocation | null;
  /**
   * Öffentliche URL des Mitarbeiterfotos, wenn „Foto in Signatur anzeigen"
   * aktiv ist (Kundenfeedback 16.10.2026 Teil 2, XXXII); sonst leer.
   */
  photoUrl?: string;
}

const EMPTY_DEFAULTS: SignatureDefaults = { signature: "", orgName: "", orgHomepage: "" };

/**
 * Kommunikationsdaten je Unternehmen. Der Cache lebt bewusst modulweit: Beim
 * Durchklicken von Nachrichten wird dieselbe Signatur sonst bei jedem
 * Antworten erneut geladen.
 */
const companyCommCache = new Map<number, any>();

/**
 * Zwischengespeicherte Kommunikationsdaten verwerfen – nach dem Bearbeiten
 * der Signatur-Vorlage aus dem E-Mail-Fenster (Kundenfeedback 24.10.2026,
 * XII), damit die neue Vorlage sofort im Entwurf erscheint.
 */
export function invalidateCompanySignature(idCompany?: number | null): void {
  if (idCompany) companyCommCache.delete(idCompany);
  else companyCommCache.clear();
}

/**
 * Standard-Standort für die Signatur: der Sitzungs-Standort, sofern der
 * Nutzer ihm zugeordnet ist, sonst der erste eigene Standort – dieselbe
 * Vorauswahl wie im E-Mail-Fenster.
 */
export function defaultSignatureLocation(locations: SignatureLocation[], sessionLocationId: number | null | undefined): SignatureLocation | null {
  if (!locations.length) return null;
  return locations.find((l) => l.id === sessionLocationId) || locations[0] || null;
}

/** Organisations-Vorgaben laden. Fehler sind unkritisch — dann bleibt es leer. */
export async function loadSignatureDefaults(idOrganization: number): Promise<SignatureDefaults> {
  if (!idOrganization) return { ...EMPTY_DEFAULTS };
  try {
    const data: any = await getRequestGeneric(`/protected/organizations/load-settings/${idOrganization}`);
    return {
      signature: data?.emailSignature || "",
      orgName: data?.name || "",
      orgHomepage: data?.homepage || "",
    };
  } catch {
    return { ...EMPTY_DEFAULTS };
  }
}

/** Standorte des angemeldeten Nutzers (Multi-Standort-Zuordnung). */
export async function loadUserLocations(): Promise<SignatureLocation[]> {
  try {
    const locs = await getRequestGeneric("/protected/users/me/locations");
    return Array.isArray(locs) ? locs : [];
  } catch {
    return [];
  }
}

/**
 * Vorlage für einen Standort auflösen. Ohne Standort (oder ohne Unternehmen
 * daran) bleibt die Organisations-Signatur stehen.
 */
export async function resolveSignatureContext(
  location: SignatureLocation | null,
  defaults: SignatureDefaults,
): Promise<SignatureContext> {
  const ctx: SignatureContext = {
    template: defaults.signature,
    companyName: "",
    website: "",
    location,
    photoUrl: "",
  };

  // Foto der Signatur (XXXII) – unabhängig vom Standort, einmal je Sitzung.
  try {
    ctx.photoUrl = activeSignaturePhotoUrl(await loadSignaturePhoto());
  } catch {
    ctx.photoUrl = "";
  }

  let loc = location;
  try {
    if (!loc) {
      // Fallback über den Session-Standort. Bewusst die Organisations-Liste
      // und nicht die eigene Standort-Zuordnung: Ist der Session-Standort
      // (noch) nicht zugeordnet, sollen seine Platzhalter trotzdem greifen.
      const store = useAuthStore();
      if (store.idLocation) {
        const locations: any = await getRequestGeneric(
          `/protected/locations/load-locations?idOrganization=${store.idOrganization}`,
        );
        loc = (locations || []).find((l: SignatureLocation) => l.id === store.idLocation) || null;
        ctx.location = loc;
      }
    }
    if (!loc?.idCompany) return ctx;

    if (!companyCommCache.has(loc.idCompany)) {
      companyCommCache.set(
        loc.idCompany,
        (await getRequestGeneric(`/protected/companies/${loc.idCompany}/communication`)) || {},
      );
    }
    const comm = companyCommCache.get(loc.idCompany);
    if (comm?.emailSignature) ctx.template = comm.emailSignature;
    if (comm?.website) ctx.website = comm.website;
    if (comm?.companyName) ctx.companyName = comm.companyName;
  } catch {
    /* still – die Organisations-Signatur bleibt als Fallback stehen */
  }
  return ctx;
}

/** Platzhalter der aufgelösten Vorlage mit den Nutzerdaten befüllen. */
export function renderUserSignature(ctx: SignatureContext, defaults: SignatureDefaults): string {
  if (!ctx.template) return "";
  const store = useAuthStore();
  const loc = ctx.location || ({} as SignatureLocation);
  const locAddress = loc.address
    || [loc.streetNo, [loc.zip, loc.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  // Foto links neben Name/Kontaktdaten (XXXII), wenn aktiviert.
  return withSignaturePhoto(renderSignatureHtml(ctx.template, {
    vorname: store.firstName,
    nachname: store.lastName,
    email: store.email,
    mobil: normalizePhone(store.signatureMobile),
    festnetz: normalizePhone(store.landline),
    titel: store.title,
    // Stellenbezeichnung, ersatzweise Abteilung (Kundenfeedback 11.09.26, I.)
    stellenbezeichnung: store.signatureJobTitle,
    benutzername: store.userName,
    // Chip "Unternehmen": Unternehmensname des Signatur-Standorts, nicht der
    // Organisationsname (Kundenreview 23.08.26, II).
    organisation: ctx.companyName || defaults.orgName || store.organizationName,
    webseite: ctx.website || defaults.orgHomepage,
    homepage: ctx.website || defaults.orgHomepage,
    // Standort-Platzhalter (standortabhängige Adress-/Kontaktdaten)
    standort: loc.name,
    standort_adresse: locAddress,
    standort_telefon: normalizePhone(loc.landline),
    standort_email: loc.email,
  }), ctx.photoUrl);
}

/**
 * Fertige Signatur des angemeldeten Nutzers für seinen Standard-Standort.
 * Bequemer Einstieg für Stellen ohne eigene Standort-Auswahl (Antwort im
 * Lesebereich, schwebendes Unterhaltungs-Fenster).
 */
export async function getUserSignatureHtml(): Promise<string> {
  const store = useAuthStore();
  // Kundenfeedback 24.10.2026 (IV.4): „da gibt es keine Signatur, wenn man
  // antwortet". Ohne Standort lief die Auflösung über die Standortliste der
  // Organisation, die nur Leitungen lesen dürfen – Mitarbeitende bekamen so
  // keine Unternehmens-Signatur. Jetzt wie im E-Mail-Fenster über die
  // eigenen Standorte des Nutzers.
  const [defaults, locations] = await Promise.all([
    loadSignatureDefaults(store.idOrganization),
    loadUserLocations(),
  ]);
  const ctx = await resolveSignatureContext(defaultSignatureLocation(locations, store.idLocation), defaults);
  return renderUserSignature(ctx, defaults);
}

/**
 * Standort, für den der Nutzer standardmäßig unterschreibt (wie im
 * E-Mail-Fenster) – für „E-Mail-Signatur bearbeiten“ außerhalb des Fensters.
 */
export async function getUserSignatureLocation(): Promise<SignatureLocation | null> {
  return defaultSignatureLocation(await loadUserLocations(), useAuthStore().idLocation);
}

/**
 * E-Mail-Signatur des angemeldeten Nutzers als Brieftext (Kundenfeedback
 * 25.10.2026 Teil 3, VII: Signatur unter dem Brief „wie bei den E-Mails“).
 * Fehler ergeben "" – dann gilt die Grußformel des Briefkopfs.
 */
export async function getUserSignatureText(): Promise<string> {
  try {
    return signatureHtmlToText(await getUserSignatureHtml());
  } catch {
    return "";
  }
}

// Der Signaturblock selbst ist reine HTML-Erzeugung und lebt im Util
// (utils/signatureTemplate) — von dort ist er ohne Store und Router testbar.
export { signatureBlockHtml };
